import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import {
  Button,
  Typography,
  Card,
  Table,
  CardContent,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableFooter,
} from '@mui/material';
import { useNavigate, Link, useParams } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useIntl } from 'react-intl';
import { SHeadTableCell } from '../../../components/styledComponents/Tables';
import Preloader from '../../../components/other/Preloader/Preloader';
import { Modal } from '../../../components/other/Modals';
import TablePaginator from '../../../components/tableComponents/TablePaginator/TablePaginator';
import CompanyBind from './CompanyBind';
import { actions as companiesActions } from '../../../store/ducks/companies.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import { IAppState } from '../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import useCrudSnackbar from '../../../hooks/useCrudSnackbar';
import homeStyles from '../../../constants/homeStyles';
import { UserListType } from '../users/constants';
import { useStylesCompanies } from './hooks/useStyles';
import { useDefineUserRole } from '../../../hooks';
import { getRole } from '../users/users/utils';

type QuizParams = {
  role: UserListType;
};

const СompaniesPage: React.FC<TPropsFromRedux> = ({
  page,
  perPage,
  total,
  companies,
  fetch,
  loading,
  delCompanySuccess,
  clearDelCompany,
  delCompany,
  delCompanyLoading,
  delCompanyError,
  clearCompanies,
  me,
  fetchMe,
  loadingMe,
  clearMe,
}) => {
  const { role } = useParams<QuizParams>();
  const roleCompany = role && getRole(role as UserListType);
  const classes1 = useStylesCompanies();
  const { classes } = classes1;
  const navigate = useNavigate();
  const homeClasses = homeStyles();
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState<number>();
  const [headerName, setHeaderName] = useState('');
  const intl = useIntl();
  const isManager = useDefineUserRole(me, 'ROLE_MANAGER');
  const isVendor = useDefineUserRole(me, ['ROLE_VENDOR']);
  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER']);
  useCrudSnackbar({
    success: delCompanySuccess,
    error: delCompanyError,
    clear: clearDelCompany,
    successMessage: intl.formatMessage({ id: 'COMPANY.DELETE.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${delCompanyError}`,
    afterSuccessOrError: () => {
      clearDelCompany();
      setOpen(false);
    },
    afterSuccess: () => fetch({ page, perPage, roles: roleCompany }),
  });

  useEffect(() => {
    fetchMe();
    return () => {
      clearMe();
      clearCompanies();
    };
  }, []);

  useEffect(() => {
    if (!me?.company) {
      if (me?.is_admin || isManager) {
        setHeaderName(
          `${intl.formatMessage({
            id: role === 'vendors' ? 'SUBMENU.VENDORS' : 'SUBMENU.CUSTOMERS',
          })} / ${intl.formatMessage({
            id: 'SUBMENU.COMPANIES',
          })}`
        );
        fetch({ page, perPage, roles: roleCompany });
      } else {
        setHeaderName(intl.formatMessage({ id: 'SUBMENU.COMPANY_SETTINGS' }));
      }
    }
  }, [me, roleCompany]);

  useEffect(() => {
    if (loadingMe) return;

    if (me?.is_admin) return;

    if (!me?.company) {
      if (isVendor || isBuyer) {
        navigate('/companies/new');
      }
    } else {
      navigate(`/companies/edit/${me.company.id?.toString()}`);
    }
  }, [me, loadingMe, navigate]);

  setLayoutSubheader({
    title: headerName,
    breadcrumb: [],
  });
  setLayoutFooter({ show: true });

  if (!me || loadingMe || !companies || loading) return <Preloader />;

  if (me && !isManager && !me.is_admin && !me.company) {
    return <CompanyBind />;
  }

  return (
    <>
      <Row>
        <Col>
          <Button
            color='primary'
            variant='contained'
            onClick={() => navigate('/companies/new')}
            className={classes.addBtn}
          >
            {intl.formatMessage({ id: 'SUBMENU.COMPANIES.ADD' })}
          </Button>
          {!companies.length ? (
            <Typography className={classes.empty} component='h5' variant='h5'>
              {intl.formatMessage({ id: 'COMMON.LIST.EMPTY' })}
            </Typography>
          ) : (
            <>
              <Card className={classes.card}>
                <CardContent className={homeClasses.classes.tableContainer}>
                  <Table aria-label='simple table'>
                    <TableHead>
                      <TableRow>
                        <SHeadTableCell className={classes.rowItems}>
                          {intl.formatMessage({ id: 'COMPANY.TABLE.ID' })}
                        </SHeadTableCell>
                        {/* <SHeadTableCell style={{ textAlign: 'center' }}>
                          {intl.formatMessage({ id: 'COMPANY.TAB.LOGO' })}
                        </SHeadTableCell> */}
                        <SHeadTableCell className={classes.rowItems}>
                          {intl.formatMessage({ id: 'COMPANY.TABLE.NAME' })}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.rowItems}>
                          {intl.formatMessage({ id: 'COMPANY.TABLE.LEGAL_PERSON' })}
                        </SHeadTableCell>
                        <SHeadTableCell className={classes.rowItems}>
                          {intl.formatMessage({ id: 'COMPANY.TABLE.INN' })}
                        </SHeadTableCell>
                        <SHeadTableCell
                          style={{ width: '300px' }}
                          className={classes.rowItems}
                        >
                          {intl.formatMessage({ id: 'COMPANY.TABLE.MANAGERS' })}
                        </SHeadTableCell>
                        <SHeadTableCell align='right' className={classes.rowItems}>
                          {intl.formatMessage({ id: 'COMPANY.TABLE.ACTIONS' })}
                        </SHeadTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {companies.map(item => (
                        <TableRow key={item.id}>
                          <TableCell component='th' scope='row' className={classes.rowItems}>
                            {item.id}
                          </TableCell>

                          <TableCell className={classes.rowItems}>
                            <Link to={`/products/catalog/company/${item.id}`}>
                              {item.brand}
                            </Link>
                          </TableCell>

                          <TableCell className={classes.rowItems}>{item.name}</TableCell>

                          <TableCell className={classes.rowItems}>{item.inn}</TableCell>

                          <TableCell className={classes.rowItems}>
                            {item.managers &&
                              item.managers.length > 0 &&
                              [...item.managers.slice(0, 3)].map(manager => (
                                <span key={manager.id} style={{ display: 'block' }}>
                                  <Link
                                    to={`/users/edit/${UserListType.MANAGER}/${manager.id}`}
                                  >
                                    {manager.first_name ||
                                    manager.last_name ||
                                    manager.middle_name
                                      ? `${manager.last_name || ''} ${
                                          manager.first_name || ''
                                        } ${manager.middle_name || ''}`
                                      : manager.email}
                                  </Link>
                                </span>
                              ))}
                          </TableCell>

                          <TableCell
                            style={{ width: '200px' }}
                            align='right'
                            className={classes.rowItems}
                          >
                            <div className={classes.actions}>
                              <IconButton
                                color='primary'
                                aria-label='previous'
                                onClick={() => navigate(`/companies/edit/${item.id}`)}
                              >
                                <EditIcon />
                              </IconButton>
                              {roleCompany !== 'ROLE_BUYER' && (
                                <>
                                  <IconButton
                                    color='primary'
                                    aria-label='previous'
                                    onClick={() => {
                                      navigate(`/products/catalog/company/${item.id}`);
                                    }}
                                  >
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      enableBackground='new 0 0 24 24'
                                      height='24px'
                                      viewBox='0 0 24 24'
                                      width='24px'
                                      fill='#205373'
                                    >
                                      <rect fill='none' height='24' width='24' />
                                      <path d='M19,5v14H5V5H19 M19,3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V5C21,3.9,20.1,3,19,3L19,3z' />
                                      <path d='M14,17H7v-2h7V17z M17,13H7v-2h10V13z M17,9H7V7h10V9z' />
                                    </svg>
                                  </IconButton>
                                </>
                              )}
                              {me.is_admin && (
                                <IconButton
                                  color='secondary'
                                  aria-label='previous'
                                  onClick={() => {
                                    setCompanyId(item.id);
                                    setOpen(true);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePaginator
                          page={page}
                          realPerPage={companies.length}
                          perPage={perPage}
                          total={total}
                          fetchRows={({ page, perPage }: { page: number; perPage: number }) =>
                            fetch({ page, perPage, roles: roleCompany })
                          }
                          label={intl.formatMessage({ id: 'COMPANY.TABLE.PERPAGE' })}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </Col>
      </Row>
      <Modal
        open={open}
        onClose={delCompanyLoading ? () => {} : () => setOpen(false)}
        title={intl.formatMessage({ id: 'COMPANY.DELETE.TITLE' })}
        loading={delCompanyLoading}
        content={
          delCompanyLoading ? undefined : intl.formatMessage({ id: 'COMPANY.DELETE.TEXT' })
        }
        actions={
          delCompanyLoading
            ? undefined
            : [
                {
                  title: intl.formatMessage({ id: 'COMMON.BUTTON.CANCEL' }),
                  onClick: () => setOpen(false),
                },
                {
                  title: intl.formatMessage({ id: 'COMMON.BUTTON.DELETE' }),
                  onClick: () => companyId && delCompany(companyId),
                },
              ]
        }
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    page: state.companies.page,
    perPage: state.companies.per_page,
    total: state.companies.total,
    companies: state.companies.companies,
    loading: state.companies.loading,
    delCompanySuccess: state.companies.delCompanySuccess,
    delCompanyLoading: state.companies.delCompanyLoading,
    delCompanyError: state.companies.delCompanyError,

    me: state.profile.me,
    loadingMe: state.profile.loading,
    successMe: state.profile.success,
  }),
  {
    fetch: companiesActions.fetchRequest,
    clearDelCompany: companiesActions.clearDelCompany,
    delCompany: companiesActions.delCompanyRequest,
    clearCompanies: companiesActions.clearCompanies,

    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(СompaniesPage);
